import { getOrderByIdThunk } from '@redux/features/order.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import React, { FC, useEffect } from 'react';

type IProps = {
    show: boolean;
    handleClose: () => void;
    order: any;
};

const ToteIssueModal: FC<IProps> = ({ show, handleClose, order }) => {
    const dispatch = useAppDispatch();
    const { order: orderData, getOrderByIdLoading } = useAppSelector((state: RootState) => state.orderState);

    // Always fetch full order details when modal opens to ensure we have latest issue data
    useEffect(() => {
        if (show && order?.id) {
            dispatch(getOrderByIdThunk(order.id));
        }
    }, [show, order?.id, dispatch]);

    // Helper function to format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to format status
    const formatStatus = (status: string | null | undefined) => {
        if (!status) return '-';
        return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Get totes with issues
    const getTotesWithIssues = () => {
        const orderToUse = orderData?.order || order;
        const totes = orderToUse?.totes || [];
        return totes.filter((tote: any) => tote.issueNote || tote.issueReportedAt);
    };

    if (!show || !order) return null;

    const totesWithIssues = getTotesWithIssues();
    const orderInfo = orderData?.order || order;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[90%] md:max-w-[1000px] md:max-h-[90vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">
                    Tote Issues - {orderInfo.id || order.id || '-'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                {getOrderByIdLoading ? (
                    <div className="mx-auto flex my-6 items-center justify-center rounded-md bg-white">
                        <div className="size-10 animate-spin rounded-full border-4 border-custom-500 border-l-transparent " />
                    </div>
                ) : totesWithIssues.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No issues found for this order.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        {totesWithIssues.map((tote: any, index: number) => (
                            <div key={tote.id || index} className="border rounded-lg p-4 bg-red-50 border-red-200">
                                <h3 className="font-bold text-lg mb-4 text-red-600">
                                    Tote #{tote.id} - Issue Report
                                </h3>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Tote ID:</span>
                                        <p className="text-gray-900">{tote.id || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Tote Barcode:</span>
                                        <p className="text-gray-900">{tote.toteBarcode || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Tote Status:</span>
                                        <p className="text-gray-900">{formatStatus(tote.status)}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Issue Reported At:</span>
                                        <p className="text-gray-900">{formatDate(tote.issueReportedAt)}</p>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <span className="text-sm font-semibold text-gray-700">Issue Note:</span>
                                    <div className="mt-2 p-3 bg-white border border-red-200 rounded-md">
                                        <p className="text-gray-900 whitespace-pre-wrap">{tote.issueNote || 'No note provided'}</p>
                                    </div>
                                </div>
                                {tote.saleOrderItems && tote.saleOrderItems.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-red-200">
                                        <h4 className="font-bold text-md mb-3 text-red-600">Items in Tote:</h4>
                                        <div className="flex flex-col gap-2">
                                            {tote.saleOrderItems.map((item: any, itemIdx: number) => (
                                                <div key={itemIdx} className="border rounded p-3 bg-white">
                                                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 text-sm">
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Product:</span>
                                                            <p className="text-gray-900">{item.product?.itemName || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Quantity:</span>
                                                            <p className="text-gray-900">{item.quantity || '-'}</p>
                                                        </div>
                                                        {item.product?.ndcCode && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">NDC Code:</span>
                                                                <p className="text-gray-900">{item.product.ndcCode}</p>
                                                            </div>
                                                        )}
                                                        {item.product?.barcode && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Barcode:</span>
                                                                <p className="text-gray-900">{item.product.barcode}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleClose}
                >
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ToteIssueModal;

